<template>
    <v-alert
        v-if="showAlert"
        :type="alertType"
        class="mx-auto text-center"
        style="position: fixed; top: 20px; left: 0; right: 0; z-index: 9999; max-width: 600px;"
        elevation="2"
        prominent
    >
        {{ alertMessage }}
    </v-alert>
    <v-container fluid>
        <v-card class="pa-4" elevation="2">
            <v-row>
                <v-col class="headline mb-4">
                    <h2>Usuarios</h2>
                </v-col>
                <v-col cols="12" sm="6" style="text-align: right">
                    <v-btn color="primary" :to="{ name: 'users.create' }">Nuevo usuario</v-btn>
                    <v-btn style="margin-left: 10px"  @click="$router.back()">Volver</v-btn>
                </v-col>
            </v-row>

            <v-alert v-if="error" type="error" dense class="mb-4">
                {{ error }}
            </v-alert>

            <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-4" />

            <!-- Filtres -->
            <v-row class="mb-4" dense>
                <v-col cols="12" sm="2">
                    <v-text-field v-model="filters.id" label="Filtrar por ID" dense clearable />
                </v-col>

                <v-col cols="12" sm="3">
                    <v-autocomplete
                        v-model="filters.name"
                        :items="nameOptions"
                        label="Filtrar por Nombre"
                        dense
                        clearable
                    />
                </v-col>

                <v-col cols="12" sm="3">
                    <v-autocomplete
                        v-model="filters.email"
                        :items="emailOptions"
                        label="Filtrar por Email"
                        dense
                        clearable
                    />
                </v-col>

                <v-col cols="12" sm="3">
                    <v-select
                        v-model="filters.role"
                        :items="roles"
                        item-title="text"
                        item-value="value"
                        label="Filtrar por Rol"
                        dense
                        clearable
                    />
                </v-col>
            </v-row>

            <v-data-table
                :headers="headers"
                :items="filteredUsers"
                class="elevation-1"
                no-data-text="No hay usuarios disponibles"
            >

                <template #item.role="{ item }">
                    {{item.role}}

                    {{ roleLabels[item.role] || '-' }}
                </template>

                <template #item.client="{ item }">
                    {{ item.client?.name || '-' }}
                </template>

                <template #item.actions="{ item }">
                    <div class="d-flex justify-end">
                        <v-tooltip text="Editar">
                            <template #activator="{ props }">
                                <v-btn
                                    icon
                                    class="ma-1"
                                    v-bind="props"
                                    :to="{ name: 'users.edit', params: { id: item.id } }"
                                >
                                    <v-icon size="18">mdi-pencil</v-icon>
                                </v-btn>
                            </template>
                        </v-tooltip>

                        <v-tooltip text="Eliminar">
                            <template #activator="{ props }">
                                <v-btn
                                    icon
                                    class="ma-1"
                                    v-bind="props"
                                    @click="deleteUser(item.id)"
                                >
                                    <v-icon size="18">mdi-delete</v-icon>
                                </v-btn>
                            </template>
                        </v-tooltip>
                    </div>
                </template>

            </v-data-table>
        </v-card>
    </v-container>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import axios from 'axios'
import { useRouter } from 'vue-router'

const router = useRouter()

import { useAlert } from '@/composables/useAlert'

const {
    alertMessage,
    alertType,
    showAlert,
    showAlertMessage
} = useAlert()

const users = ref([])
const error = ref(null)
const token = localStorage.getItem('token')
const loading = ref(false)

const filters = ref({
    id: '',
    name: '',
    email: '',
    role: ''
})

const roles = [
    { text: 'Administrador', value: 'admin' },
    { text: 'Cliente', value: 'client' },
    { text: 'Calidad', value: 'quality' },
    { text: 'Proveedor', value: 'provider' },
    { text: 'Almacen BCN', value: 'warehouseBCN' },
    { text: 'Almacen MAD', value: 'warehouseMAD' },
    { text: 'Producción', value: 'production' },
    { text: 'Planificación', value: 'planning' },
    { text: 'Comercial', value: 'comercial' },
    { text: 'BackOffice', value: 'backoffice' },
    { text: 'Gerencia', value: 'gerencia' },
    { text: 'Compres', value: 'compres' }
]

const roleLabels = {
    admin: 'Administrador',
    client: 'Cliente',
    quality: 'Caliadd',
    provider: 'Proveedor',
    warehouseBCN: 'Almacen BCN',
    warehouseMAD: 'Almacen MAD',
    production: 'Producción',
    planning: 'Planificación',
    comercial: 'Comercial',
    backoffice: 'BackOffice',
    gerencia: 'Gerencia',
    compres: 'Compres'
}

const headers = [
    { title: 'ID', key: 'id' , sortable: true},
    { title: 'Nombre', key: 'name' , sortable: true},
    { title: 'Email', key: 'email' , sortable: true},
    { title: 'Rol', key: 'role' , sortable: true},
    { title: 'Client', key: 'client' , sortable: true},
    { title: 'Acciones', key: 'actions', sortable: false }
]

const nameOptions = computed(() => {
    return [...new Set(users.value.map(u => u.name).filter(Boolean))]
})

const emailOptions = computed(() => {
    return [...new Set(users.value.map(u => u.email).filter(Boolean))]
})

const filteredUsers = computed(() => {
    const lower = val => (val || '').toString().toLowerCase()

    return users.value.filter(user => {
        return (
            (!filters.value.id || user.id.toString().includes(filters.value.id)) &&
            (!filters.value.name || lower(user.name).includes(lower(filters.value.name))) &&
            (!filters.value.email || lower(user.email).includes(lower(filters.value.email))) &&
            (!filters.value.role || lower(user.role) === lower(filters.value.role))
        )
    })
})

const loadUser = async () => {
    try {
        loading.value = true
        error.value = null

        const response = await axios.get('/api/admin/users', {
            withCredentials: true,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            }
        })

        //console.log(response)

        users.value = response.data
    } catch (e) {
        console.error('Error loading users:', e)
        showAlertMessage('Error cargando los usuarios.', 'error')
        if (e.response?.status === 401) {
            localStorage.removeItem('token')
            await router.push('/')
        }
    } finally {
        loading.value = false
    }
}

const deleteUser = async (id) => {
    if (!confirm('¿Estás seguro que quieres eliminar este usuario?')) return

    try {
        await axios.delete(`/api/admin/users/${id}`, {
            withCredentials: true,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            }
        })

        await loadUser()
    } catch (e) {
        console.error('Error eliminando el usuario:', e)

        if (e.response?.status === 401) {
            localStorage.removeItem('token')
            await router.push('/')
        }

        showAlertMessage('Error eliminando al usuario.', 'error')
    }
}

onMounted(loadUser)
</script>
